'use client';
import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';
import useTickerList from '@/features/ticker/model/useTickerList';
import { storeFutuExchange } from '@/shared/store/exchange';
import { BOTS, DATA_STATUS, HOME } from '@/shared/utils/const';
import {
  ConvetDotSize,
  formatNumberWithUnitsFutures,
  formatPriceChangePercent,
} from '@/shared/utils/formatter';
import { useChartModal } from '@/widget/chart/model/ChartModalContext';
import { useSession } from '@/app/providers';
import { storeFutuUserInfo } from '@/shared/store/userInfo';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useManagementDataStore } from '@/features/management/model/managementData';
import { useT } from '@/shared/i18n/I18nProvider';
import { PostFavoriteList } from '@/entities/favorite/api/postFavoriteList';
import { PostFavorite } from '@/entities/favorite/api/postFavorite';
import { DeleteFavorite } from '@/entities/favorite/api/deleteFavorite';

// 즐겨찾기 아이콘 경로
const STAR_ENABLE = '/images/star-enable.svg';
const STAR_DISABLE = '/images/star-disable.svg';

// TODO: limitedMarketList
export const TickerList = ({ isTopRanking = false }: { isTopRanking?: boolean }) => {
  const { showMarketList, searchTerm, setSearchTerm, clickOrder, sortOfItem, sortOfDesc } = useTickerList();
  const { getTickSize } = storeFutuExchange();
  const { openChartModal } = useChartModal()
  const { setFutuSymbol } = storeFutuUserInfo()
  const session = useSession()

  // Provider에서 가져옴
  const t = useT();

  // 즐겨찾기 상태
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // 즐겨찾기 목록 불러오기
  const fetchFavorites = useCallback(async () => {
    try {
      const res = await PostFavoriteList();
      if (res?.result_code === '100' && res.list) {
        setFavorites(new Set(res.list.map(item => item.symbol)));
      }
    } catch (err) {
      console.error('즐겨찾기 목록 조회 실패:', err);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // 즐겨찾기 토글
  const toggleFavorite = async (e: React.MouseEvent, symbol: string) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지
    
    try {
      if (favorites.has(symbol)) {
        // 즐겨찾기 삭제
        const res = await DeleteFavorite(symbol);
        if (res?.result_code === '100') {
          setFavorites(prev => {
            const next = new Set(prev);
            next.delete(symbol);
            return next;
          });
        }
      } else {
        // 즐겨찾기 추가
        const res = await PostFavorite({ list: [{ symbol }] });
        if (res?.result_code === '100') {
          setFavorites(prev => new Set(prev).add(symbol));
        }
      }
    } catch (err) {
      console.error('즐겨찾기 토글 실패:', err);
    }
  };

  const handleClick = (symbol: string) => {
    setFutuSymbol(symbol, session)

    // 임시저장 데이터가 있으면 봇설정에 적용 (코인은 무조건 LONG으로 찾기)
    const { applyTemporaryBySymbol } = useManagementDataStore.getState()
    const hasTemporaryData = applyTemporaryBySymbol(symbol, 'LONG')

    if (!useIsMobile()) {
      return
    }
    openChartModal()
  };

  const handleClear = () => {
    setSearchTerm('')
  }


  // isTopRanking이 true면 상위 5개만 출력 , 아니면 전체 리스트
  const filteredList = showFavoritesOnly
    ? Array.from(showMarketList.values()).filter(token => favorites.has(token.symbol))
    : Array.from(showMarketList.values());
  
  const displayList = isTopRanking
    ? filteredList.slice(0, 5)
    : filteredList;


  return (
    <>
      <div className="mo-main-search">
        <Image src={HOME.mo.search} width={14} height={14} alt="search" />
        <input
          type="text"
          value={searchTerm}
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
          //return 클릭시 키보드 아래로
          enterKeyHint="done"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
        />
        {searchTerm && (
          <button className="icon right" onClick={handleClear}>
            <Image src={BOTS.mo.coinDelete} width={14} height={14} alt="코인 지우기" />
          </button>
        )}
      </div>
      <div className="mo-ticker-header ">
        <table>
          <thead>
            <tr>
              <th className="text-left">
                <button 
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)} 
                  className="inline-flex align-middle mr-[4px]"
                >
                  <Image 
                    src={showFavoritesOnly ? STAR_ENABLE : STAR_DISABLE} 
                    width={14} 
                    height={14} 
                    alt="즐겨찾기 필터" 
                  />
                </button>
                <button onClick={() => clickOrder('symbol')} className="inline-flex items-center justify-center">
                  {t('Ticker.Name')}
                  <div className="mo-sort-img">
                    <Image
                      src={HOME.mo.sortUp}
                      width={6}
                      height={6}
                      className={sortOfItem === 'symbol' && sortOfDesc === 'asc' ? 'active' : ''}
                      alt="sortup"
                    />
                    <Image
                      src={HOME.mo.sortDown}
                      width={6}
                      height={6}
                      className={sortOfItem === 'symbol' && sortOfDesc === 'desc' ? 'active' : ''}
                      alt="sortup"
                    />
                  </div>
                </button>
              </th>
              <th className="text-right">
                <button onClick={() => clickOrder('last')} className="inline-flex items-center justify-center">
                  {t('Ticker.CurrentPrice')}
                  <div className="mo-sort-img">
                    <Image
                      src={HOME.mo.sortUp}
                      width={6}
                      height={6}
                      className={sortOfItem === 'last' && sortOfDesc === 'asc' ? 'active' : ''}
                      alt="sortup"
                    />
                    <Image
                      src={HOME.mo.sortDown}
                      width={6}
                      height={6}
                      className={sortOfItem === 'last' && sortOfDesc === 'desc' ? 'active' : ''}
                      alt="sortup"
                    />
                  </div>
                </button>
                <span className="align-middle mx-[4px]">/</span>
                <button onClick={() => clickOrder('volume')} className="inline-flex items-center justify-center">
                  {t('Ticker.Volume')}
                  <div className="mo-sort-img">
                    <Image
                      src={HOME.mo.sortUp}
                      width={6}
                      height={6}
                      className={sortOfItem === 'volume' && sortOfDesc === 'asc' ? 'active' : ''}
                      alt="sortup"
                    />
                    <Image
                      src={HOME.mo.sortDown}
                      width={6}
                      height={6}
                      className={sortOfItem === 'volume' && sortOfDesc === 'desc' ? 'active' : ''}
                      alt="sortup"
                    />
                  </div>
                </button>
              </th>
              <th className="text-right">
                <button onClick={() => clickOrder('changeRate')} className="inline-flex items-center justify-center">
                  {t('Ticker.24H')}
                  <div className="mo-sort-img">
                    <Image
                      src={HOME.mo.sortUp}
                      width={6}
                      height={6}
                      className={sortOfItem === 'changeRate' && sortOfDesc === 'asc' ? 'active' : ''}
                      alt="sortup"
                    />
                    <Image
                      src={HOME.mo.sortDown}
                      width={6}
                      height={6}
                      className={sortOfItem === 'changeRate' && sortOfDesc === 'desc' ? 'active' : ''}
                      alt="sortup"
                    />
                  </div>
                </button>
              </th>
            </tr>
          </thead>
        </table>
        <div className="mo-scroll-ticker-table" data-ptr-scroller>
          <table>
            <tbody>
              {displayList.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center">
                    <div className="flex flex-col items-center justify-center gap-[12px] h-[234px] pt-[16px] pb-[8px]">
                      <Image src={DATA_STATUS.mo.nodata} width={48} height={48} alt="nodata" />
                      <p className="text-[14px] text-font-sub">No data.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayList.map((token) => (
                  <tr key={token.symbol} onClick={() => handleClick(token.symbol)}>
                    <td className="text-left text-font-basic cursor-pointer">
                      <span className="inline-flex items-center">
                        <button 
                          onClick={(e) => toggleFavorite(e, token.symbol)} 
                          className="inline-flex items-center justify-center mr-[4px] shrink-0"
                        >
                          <Image 
                            src={favorites.has(token.symbol) ? STAR_ENABLE : STAR_DISABLE} 
                            width={14} 
                            height={14} 
                            alt="즐겨찾기" 
                          />
                        </button>
                        {token.symbol}
                      </span>
                    </td>
                    <td className={`text-right ${Number(token.change) >= 0 ? 'text-chart-long' : 'text-chart-short'}`}>
                      <p className="font-semibold leading-none">
                        {ConvetDotSize(token.last, getTickSize(token.symbol))}
                      </p>
                      <span className="font-normal leading-none">
                        {formatNumberWithUnitsFutures(token.vol ?? '0')}
                      </span>
                    </td>
                    <td className="text-center text-font-basic max-w-[78.88px]">
                      <span className={`change ${Number(token.change) >= 0 ? 'long' : 'short'}`}>
                        {formatPriceChangePercent(token.change ?? '0')}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
